import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, Scan, Truck, BoxSelect, LogOut, User, Wifi, WifiOff, ClipboardList, ClipboardCheck, XCircle, Boxes, PackageCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import apiClient from '@/services/apiClient';

const MobileWMSDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [online, setOnline] = useState(navigator.onLine);
  const [stats, setStats] = useState({
    pendingSFGSlips: 0,
    pendingFGSlips: 0,
    pendingPutAwaySlips: 0,
    cancelledPutAwayItems: 0,
    pendingSeasonalSlips: 0
  });

  const fetchStats = async () => {
    try {
      const [sfgRes, fgRes, putAwayRes, cancelledRes, seasonalRes] = await Promise.all([
        apiClient.get('/wms/pick-slips?status=PENDING'),
        apiClient.get('/wms/fg/pick-slips?status=IMPORTED'),
        apiClient.get('/wms/fg/put-away/my-slips'),
        apiClient.get('/wms/fg/put-away/cancelled-pickslip-pending').catch(() => ({ data: { total_pending: 0 } })),
        apiClient.get('/wms/seasonal-storage/picking/my-slips').catch(() => ({ data: { total: 0 } }))
      ]);
      setStats({
        pendingSFGSlips: sfgRes.data.total || 0,
        pendingFGSlips: fgRes.data.total || 0,
        pendingPutAwaySlips: putAwayRes.data.count || 0,
        cancelledPutAwayItems: cancelledRes.data.total_pending || 0,
        pendingSeasonalSlips: seasonalRes.data.total || 0
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }

    const handleOnline = () => setOnline(true);
    const handleOffline = () => setOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    fetchStats();

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  // Determine which modules user can access
  const userModules = user?.modules || [];
  const userRole = user?.role || '';
  
  // Admin/Supervisor roles can see all tiles
  const isAdmin = ['Admin', 'Supervisor', 'Section Incharge', 'Section Supervisor', 'WMS Admin', 'WMS Supervisor'].includes(userRole);
  
  // Check if user has SFG or FG WMS access
  const hasSFGAccess = isAdmin || userModules.includes('sfg_wms');
  const hasFGAccess = isAdmin || userModules.includes('fg_wms');

  // Define all available modules with their access type
  const allModules = [
    {
      id: 'picker-dashboard',
      title: 'My Pick Tasks',
      subtitle: 'View assigned pick slips',
      icon: ClipboardList,
      route: '/wms/picker',
      color: 'from-indigo-500 to-indigo-700',
      badge: null,
      accessType: 'sfg' // SFG pickers see this
    },
    {
      id: 'sfg-putaway',
      title: 'SFG Put-Away',
      subtitle: 'Store bundles in SFG',
      icon: Package,
      route: '/wms/mobile/sfg-putaway',
      color: 'from-purple-500 to-purple-700',
      badge: null,
      accessType: 'sfg'
    },
    {
      id: 'sfg-picking',
      title: 'SFG Picking',
      subtitle: 'Pick for replenishment',
      icon: Truck,
      route: '/wms/mobile/sfg-picking',
      color: 'from-cyan-500 to-cyan-700',
      badge: stats.pendingSFGSlips > 0 ? stats.pendingSFGSlips : null,
      accessType: 'sfg'
    },
    {
      id: 'put-away-slips',
      title: 'GT Put Away',
      subtitle: 'Scan bundles from Goods Transfer',
      icon: ClipboardCheck,
      route: '/wms/fg/put-away-mobile',
      color: 'from-emerald-500 to-emerald-700',
      badge: stats.pendingPutAwaySlips > 0 ? stats.pendingPutAwaySlips : null,
      accessType: 'fg' // FG pickers see this
    },
    {
      id: 'fg-putaway',
      title: 'FG Put-Away (USN)',
      subtitle: 'Put away individual USN packs',
      icon: BoxSelect,
      route: '/wms/fg-putaway',
      color: 'from-teal-500 to-teal-700',
      badge: null,
      accessType: 'fg'
    },
    {
      id: 'fg-picking',
      title: 'FG Picking',
      subtitle: 'Pick for dispatch',
      icon: Scan,
      route: '/wms/mobile/fg-picking',
      color: 'from-amber-500 to-amber-700',
      badge: stats.pendingFGSlips > 0 ? stats.pendingFGSlips : null,
      accessType: 'fg'
    },
    {
      id: 'pick-and-pack',
      title: 'Pick & Pack',
      subtitle: 'Confirm qty, then scan-pack',
      icon: PackageCheck,
      route: '/wms/mobile/pick-and-pack',
      color: 'from-fuchsia-500 to-violet-700',
      badge: stats.pendingFGSlips > 0 ? stats.pendingFGSlips : null,
      accessType: 'fg'
    },
    {
      id: 'seasonal-picking',
      title: 'Seasonal Picking',
      subtitle: 'Pick packed seasonal boxes',
      icon: Boxes,
      route: '/wms/seasonal-bundle/picking',
      color: 'from-rose-500 to-rose-700',
      badge: stats.pendingSeasonalSlips > 0 ? stats.pendingSeasonalSlips : null,
      accessType: 'fg'
    },
    {
      id: 'cancelled-putaway',
      title: 'Cancelled Slip Queue',
      subtitle: 'Put away from cancelled slips',
      icon: XCircle,
      route: '/wms/mobile/cancelled-putaway',
      color: 'from-orange-500 to-orange-700',
      badge: stats.cancelledPutAwayItems > 0 ? stats.cancelledPutAwayItems : null,
      accessType: 'fg'
    }
  ];

  // Filter modules based on user access
  const modules = allModules.filter(module => {
    if (isAdmin) return true; // Admin sees all
    if (module.accessType === 'sfg' && hasSFGAccess) return true;
    if (module.accessType === 'fg' && hasFGAccess) return true;
    return false;
  });

  return (
    <div className="min-h-screen bg-gray-900 text-white flex flex-col">
      {/* Header */}
      <header className="bg-gray-800 px-4 py-3 flex items-center justify-between safe-area-inset-top">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-xl flex items-center justify-center">
            <Package className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-bold">WMS Mobile</h1>
            <p className="text-xs text-gray-400">Warehouse Operations</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {online ? (
            <Wifi className="w-5 h-5 text-green-400" />
          ) : (
            <WifiOff className="w-5 h-5 text-red-400" />
          )}
        </div>
      </header>

      {/* User Info */}
      <div className="bg-gray-800/50 px-4 py-3 flex items-center justify-between border-b border-gray-700">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-gray-700 rounded-full flex items-center justify-center">
            <User className="w-4 h-4 text-gray-400" />
          </div>
          <div>
            <p className="text-sm font-medium">{user?.name || 'Staff'}</p>
            <p className="text-xs text-gray-500">{user?.role || 'Worker'}</p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleLogout}
          className="text-gray-400 hover:text-white hover:bg-gray-700"
        >
          <LogOut className="w-4 h-4" />
        </Button>
      </div>

      {/* Main Content */}
      <main className="flex-1 p-4 space-y-4">
        <h2 className="text-sm font-medium text-gray-400 uppercase tracking-wider">
          Select Task
        </h2>

        <div className="grid grid-cols-1 gap-4">
          {modules.map((module) => {
            const Icon = module.icon;
            return (
              <button
                key={module.id}
                onClick={() => navigate(module.route)}
                className={`relative bg-gradient-to-r ${module.color} rounded-2xl p-6 text-left transition-transform active:scale-[0.98] shadow-lg`}
                data-testid={`mobile-wms-${module.id}`}
              >
                {module.badge && (
                  <div className="absolute top-3 right-3 w-7 h-7 bg-red-500 rounded-full flex items-center justify-center text-sm font-bold animate-pulse">
                    {module.badge}
                  </div>
                )}
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-sm">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold">{module.title}</h3>
                    <p className="text-sm text-white/80">{module.subtitle}</p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-800 px-4 py-3 text-center safe-area-inset-bottom">
        <Button
          variant="ghost"
          onClick={() => navigate('/wms')}
          className="text-gray-400 hover:text-white text-sm"
        >
          Switch to Desktop View
        </Button>
      </footer>
    </div>
  );
};

export default MobileWMSDashboard;
